import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ReviewSection } from '../../components/ReviewSection';
import { ObjectPrivacyControls } from '../../components/ObjectPrivacyControls';
import { ReviewHero } from '../../components/edit-object/ReviewHero';
import { useReviewData } from '../../hooks/useReviewData';
import { useFieldVisibility } from '../../hooks/useFieldVisibility';
import { useUpload } from '../../hooks/useUpload';
import { useUploadImageVisibility } from '../../hooks/useUploadImageVisibility';
import { getCrossOrigin } from '../../utils/imageUtils';
import {
  formatControlledVocabulary,
  formatSingleControlledVocabulary,
  formatCreationDate,
  formatCreationDateThumbnail,
  formatHeroDimensions,
  formatSignatures,
  truncateText
} from '../../utils/catalogFormatters';

interface ReviewTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
  onNavigateToTab: (tabIndex: number) => void;
}


/**
 * Per-row interactive chip used inside Review rows to toggle a field's
 * public/private state in place. Falls back to a static badge when no
 * `onToggle` is supplied (used for hard-coded private rows like
 * "Private Notes" where the visibility is structural, not user-editable).
 */
const RowVisibilityChip: React.FC<{
  visibility: 'public' | 'private';
  onToggle?: () => void;
  fieldPath?: string;
}> = ({ visibility, onToggle, fieldPath }) => {
  const isPublic = visibility === 'public';
  const label = isPublic ? 'PUBLIC' : 'PRIVATE';
  const testId = fieldPath
    ? `review-field-chip-${fieldPath}`
    : `review-field-chip-static-${visibility}`;
  return (
    <Chip
      data-testid={testId}
      data-visibility={visibility}
      icon={isPublic ? <Visibility sx={{ fontSize: 14 }} /> : <VisibilityOff sx={{ fontSize: 14 }} />}
      label={label}
      size="small"
      variant="outlined"
      color={isPublic ? 'success' : 'default'}
      clickable={Boolean(onToggle)}
      onClick={onToggle}
      sx={{ height: 22, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}
    />
  );
};

/**
 * Reusable label/value/chip row for review display. When `fieldPath` is
 * provided, the chip is interactive — clicking it toggles the field's
 * visibility through `useFieldVisibility`. Without `fieldPath`, the chip
 * is static (used for structurally private rows).
 */
const ReviewField: React.FC<{
  label: string;
  visibility?: 'public' | 'private';
  fieldPath?: string;
  children: React.ReactNode;
}> = ({ label, visibility, fieldPath, children }) => {
  const { getFieldVisibility, updateFieldVisibility } = useFieldVisibility();
  const effectiveVisibility: 'public' | 'private' = fieldPath
    ? getFieldVisibility(fieldPath)
    : (visibility ?? 'private');
  // `useFieldVisibility().updateFieldVisibility` already does optimistic
  // update + rollback + writes the failure to context.error — see
  // FieldVisibilityContext. Awaiting / catching here would duplicate that
  // handling. The `void` is intentional, not a swallowed promise.
  const onToggle = fieldPath
    ? () => {
        void updateFieldVisibility(
          fieldPath,
          effectiveVisibility === 'public' ? 'private' : 'public',
        );
      }
    : undefined;
  const showChip = Boolean(fieldPath || visibility);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '180px 1fr auto' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        columnGap: 2,
        rowGap: 0.25,
        py: 0.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, letterSpacing: '0.02em' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
        {children}
      </Typography>
      {showChip && (
        <RowVisibilityChip
          visibility={effectiveVisibility}
          onToggle={onToggle}
          fieldPath={fieldPath}
        />
      )}
    </Box>
  );
};

const EmptyState: React.FC = () => (
  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
    Not yet added
  </Typography>
);

export const ReviewTab: React.FC<ReviewTabProps> = ({ catalogItemId, ownerId, onNavigateToTab }) => {
  const navigate = useNavigate();
  const { uploadedImages, aliCatalogNumber, getCoverImage } = useUpload();
  const {
    toggleVisibility: toggleImageVisibility,
    error: imageVisibilityError,
  } = useUploadImageVisibility(ownerId);
  const {
    catalogItem,
    description,
    measurements,
    keywords,
    provenance,
    additionalInfo,
    exhibitions,
    loading,
    error
  } = useReviewData(catalogItemId, ownerId, {
    // Defer the initial fetch by 300 ms to allow any in-flight flush saves
    // triggered when the user navigates away from a previous wizard tab to
    // complete before the Review tab reads the data back. Without this delay
    // the GET requests race against the departing tab's fire-and-forget POSTs
    // and can return stale/empty provenance or keyword data (#1060).
    mountDelay: 300,
  });

  const coverImage = getCoverImage() as { imageId: string; thumbnailUrl?: string; file?: File | null } | null;

  // Memoize cover image URL to prevent memory leaks from repeated createObjectURL calls
  const coverImageUrl = useMemo(() => {
    if (coverImage?.thumbnailUrl) return coverImage.thumbnailUrl;
    if (coverImage?.file) return window.URL.createObjectURL(coverImage.file);
    return null;
  }, [coverImage]);

  // Cleanup blob URL on unmount or when it changes
  useEffect(() => {
    return () => {
      if (coverImageUrl?.startsWith('blob:')) URL.revokeObjectURL(coverImageUrl);
    };
  }, [coverImageUrl]);

  // Use upload context images, fall back to server-side catalogItem.images for page refresh scenarios
  const imagesFromUpload = uploadedImages as Array<{ imageId: string; thumbnailUrl?: string; file?: File | null; status: string }>;
  const serverImages = (catalogItem as Record<string, unknown> | null)?.images as Array<{ imageId: string; thumbnailUrl?: string }> | undefined;
  const displayImages = imagesFromUpload.length > 0 ? imagesFromUpload : (serverImages ?? []);
  const hasUploadedImages = displayImages.length > 0;

  // Memoize blob URLs for uploaded images to prevent memory leaks
  const imageBlobUrls = useMemo(() => {
    const urls = new Map<string, string>();
    for (const img of imagesFromUpload) {
      if (!img.thumbnailUrl && img.file) {
        urls.set(img.imageId, window.URL.createObjectURL(img.file));
      }
    }
    return urls;
  }, [imagesFromUpload]);

  // Cleanup blob URLs on unmount or when they change
  useEffect(() => {
    return () => {
      for (const url of imageBlobUrls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [imageBlobUrls]);

  const itemStatus = (catalogItem as Record<string, unknown> | null)?.status as string | undefined;

  // Cleared placeholder
  if (itemStatus === 'CLEARED') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{
          width: 120,
          height: 120,
          mx: 'auto',
          mb: 2,
          bgcolor: 'action.hover',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed',
          borderColor: 'divider',
        }}>
          <Typography variant="h4" color="text.disabled">?</Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Available Catalog Number
        </Typography>
        {aliCatalogNumber && (
          <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {aliCatalogNumber}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This catalog entry has been cleared and is available for reuse.
          Upload new images to begin cataloging.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onNavigateToTab(0)}
        >
          Upload New Images
        </Button>
      </Box>
    );
  }

  // Empty state when no catalog item
  if (!catalogItemId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No catalog item to review
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload images first to start cataloging your artwork.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onNavigateToTab(0)}
        >
          Go to Upload
        </Button>
      </Box>
    );
  }

  // Derive sliced exhibition data
  const exhibitionList = exhibitions?.exhibitions?.filter(e => e.type === 'exhibition') ?? [];
  const publicationList = exhibitions?.exhibitions?.filter(e => e.type === 'publication') ?? [];
  const awardList = exhibitions?.exhibitions?.filter(
    e => e.type === 'award' && e.publicationText?.trim()
  ) ?? [];
  const relatedWorks = additionalInfo?.relatedWorks ?? [];

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Master object-level privacy controls — labeled VISIBILITY row matching
          the Review mockup. Per-section + per-row toggles remain available inside
          each section's accordion. */}
      <Box
        data-testid="review-visibility-row"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 2,
          mb: 2,
          py: 1.25,
          px: 1.5,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          VISIBILITY
        </Typography>
        <ObjectPrivacyControls />
      </Box>

      {/* Hero header — design-aligned rs-hero block. Replaces the small
          reminder strip with a 160px cover, serif title, italic creator
          line, and meta facts (work type · dimensions). Used by both the
          legacy wizard and the redesigned edit-object flow. */}
      <ReviewHero
        coverImageUrl={coverImageUrl}
        coverCrossOrigin={coverImageUrl ? getCrossOrigin(coverImageUrl) : undefined}
        title={description?.title}
        subtitle={(() => {
          const creator = description?.creators?.[0]?.name?.trim();
          // GH#1229: hero surfaces show year-only (ranges as start–end),
          // matching the object-details hero and grid thumbnails; the full
          // date stays on the "Creation Date" field row below.
          const date = description?.creationDate
            ? formatCreationDateThumbnail(description.creationDate)
            : null;
          if (creator && date) return `${creator} · ${date}`;
          return creator || date || undefined;
        })()}
        metaLine={(() => {
          const workType = description?.workType
            ? formatSingleControlledVocabulary(description.workType)
            : null;
          const medium = description?.mediaDescription?.trim() || null;
          const dims = measurements?.dimensions?.length
            ? formatHeroDimensions(measurements)
            : null;
          const parts: React.ReactNode[] = [];
          if (workType) parts.push(<strong key="wt">{workType}</strong>);
          if (medium) parts.push(medium);
          if (dims) parts.push(dims);
          if (parts.length === 0) return undefined;
          return parts.reduce<React.ReactNode[]>((acc, p, i) => {
            if (i > 0) acc.push(<span key={`sep-${i}`}> · </span>);
            acc.push(p);
            return acc;
          }, []);
        })()}
        metaLineSecondary={(() => {
          const sig = description?.signatures && description.signatures.length > 0
            ? formatSignatures(description.signatures)
            : null;
          // Surface a 'Unique work' fallback when no editions are recorded.
          // When editions exist the per-edition rows in Artwork Details below
          // carry the detail; the hero meta line stays out of the way.
          const editionsLine =
            description?.editions && description.editions.length > 0 ? null : 'Unique work';
          const parts = [sig, editionsLine].filter(Boolean);
          if (parts.length === 0) return undefined;
          return parts.join(' · ');
        })()}
        catalogId={aliCatalogNumber || undefined}
      />

      {/* 1. Images — per-image public/private toggles. The section-level
          visibility chip was removed in favor of granular per-image control;
          each thumbnail carries its own clickable PUBLIC/PRIVATE chip that
          routes through `useUploadImageVisibility().toggleVisibility`. */}
      <ReviewSection
        title="Images"
        tabIndex={0}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={!loading && !hasUploadedImages}
        emptyMessage="No images uploaded yet"
      >
        {hasUploadedImages && (
          <Box>
            {imageVisibilityError && (
              <Alert
                severity="error"
                data-testid="review-image-visibility-error"
                sx={{ mb: 1 }}
              >
                {imageVisibilityError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhotoIcon color="action" />
              <Typography variant="body2">
                {displayImages.length} image{displayImages.length !== 1 ? 's' : ''} uploaded
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {displayImages.map((img: { imageId: string; thumbnailUrl?: string; uploadedAt?: string; visibility?: 'public' | 'private' }) => {
                const url = img.thumbnailUrl ?? imageBlobUrls.get(img.imageId) ?? null;
                if (!url) return null;
                const visibility: 'public' | 'private' = img.visibility ?? 'private';
                const isPublic = visibility === 'public';
                return (
                  <Box key={img.imageId} sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      component="img"
                      crossOrigin={getCrossOrigin(url)}
                      src={url}
                      alt="Uploaded artwork"
                      sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                    <Chip
                      data-testid={`review-image-visibility-${img.imageId}`}
                      data-visibility={visibility}
                      icon={isPublic ? <Visibility sx={{ fontSize: 14 }} /> : <VisibilityOff sx={{ fontSize: 14 }} />}
                      label={isPublic ? 'PUBLIC' : 'PRIVATE'}
                      size="small"
                      variant="outlined"
                      color={isPublic ? 'success' : 'default'}
                      clickable
                      onClick={() => { void toggleImageVisibility(img.imageId); }}
                      sx={{ height: 22, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' }}
                    />
                    {img.uploadedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {new Date(img.uploadedAt).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </ReviewSection>

      {/* 2. Artwork Details — title, creators, dates, inscriptions, media description, dimensions */}
      <ReviewSection
        title="Artwork Details"
        tabIndex={1}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={!loading && !description?.title && (!description?.creators || description.creators.length === 0) && (!measurements?.dimensions || measurements.dimensions.length === 0) && (!keywords?.keywords || keywords.keywords.length === 0)}
        emptyMessage="No artwork details entered yet"
      >
        <Stack spacing={1}>
          {description?.title && (
            <ReviewField label="Title" fieldPath="describe.title">{description.title}</ReviewField>
          )}
          {description?.alternativeTitles && description.alternativeTitles.length > 0 && (
            <ReviewField label="Alternative Titles" fieldPath="describe.alternativeTitles">
              {description.alternativeTitles.join(', ')}
            </ReviewField>
          )}
          {description?.creators && description.creators.length > 0 && (
            <ReviewField label="Creators" fieldPath="describe.creators">
              {description.creators.map(c => c.name).filter(Boolean).join(', ')}
            </ReviewField>
          )}
          {description?.creationDate && (description.creationDate.value || description.creationDate.display) && (
            <ReviewField label="Creation Date" fieldPath="describe.creationDate">{formatCreationDate(description.creationDate)}</ReviewField>
          )}
          {description?.signatures && description.signatures.length > 0 && (
            <ReviewField label="Inscriptions" fieldPath="describe.signature">
              {formatSignatures(description.signatures) || 'None'}
            </ReviewField>
          )}
          {description?.mediaDescription && (
            <ReviewField label="Media Description" fieldPath="describe.mediaDescription">{description.mediaDescription}</ReviewField>
          )}
          {measurements?.dimensions && measurements.dimensions.length > 0 && (
            <>
              <ReviewField label="Dimensions" fieldPath="measure.dimensions">{formatHeroDimensions(measurements)}</ReviewField>
              {measurements.dimensions
                .filter(d => !['height', 'width', 'depth'].includes(d.type.toLowerCase()) && d.value)
                .map((d, i) => (
                  <ReviewField key={i} label={d.type.charAt(0).toUpperCase() + d.type.slice(1)} fieldPath="measure.dimensions">
                    {d.fraction ? `${d.value} ${d.fraction}` : d.value}{d.unit ? ` ${d.unit}` : ` ${measurements.unit}`}
                  </ReviewField>
                ))
              }
              {measurements.hasDifferentSubstrate && measurements.substrateMeasurements && (
                <ReviewField label="Substrate Dimensions" fieldPath="measure.dimensions">
                  {formatHeroDimensions(measurements.substrateMeasurements)}
                </ReviewField>
              )}
            </>
          )}
          {keywords?.keywords && keywords.keywords.length > 0 && (
            <ReviewField label="Keywords" fieldPath="catalogKeywords.keywords">
              {keywords.keywords.join(', ')}
            </ReviewField>
          )}
          {description?.editions && description.editions.length > 0 && (
            <>
              <ReviewField label="Multiples / Editions" fieldPath="describe.editions">
                {description.editions.length} edition{description.editions.length === 1 ? '' : 's'}
              </ReviewField>
              {description.editions.map((ed, i) => {
                const type = (ed.type || '').trim() || 'Numbered';
                const count = Number.isFinite(ed.count) ? ed.count : '?';
                const total = Number.isFinite(ed.total) ? ed.total : '?';
                return (
                  <ReviewField
                    key={`edition-${i}`}
                    label={`Edition ${i + 1}`}
                    fieldPath="describe.editions"
                  >
                    {`${type}: ${count} of ${total}`}
                  </ReviewField>
                );
              })}
            </>
          )}
        </Stack>
      </ReviewSection>

      {/* 3. Getty Vocab — work type, techniques, materials, substrate.
          When the user has selected nothing, the section is suppressed
          entirely (no empty-state placeholder) — the absence is clearer
          than a header followed by "No Getty vocabulary selected yet". */}
      {!loading &&
        description &&
        (description.workType ||
          (description.techniques && description.techniques.length > 0) ||
          (description.materials && description.materials.length > 0) ||
          (description.substrate && description.substrate.length > 0)) && (
          <ReviewSection
            title="Getty Vocab"
            tabIndex={2}
            onEdit={onNavigateToTab}
            isLoading={loading}
            isEmpty={false}
          >
            <Stack spacing={1}>
              {description.workType && (
                <ReviewField label="Getty Work Type" fieldPath="describe.workType">
                  {formatSingleControlledVocabulary(description.workType)}
                </ReviewField>
              )}
              {description.techniques && description.techniques.length > 0 && (
                <ReviewField label="Getty Techniques" fieldPath="describe.techniques">
                  {formatControlledVocabulary(description.techniques as unknown[])}
                </ReviewField>
              )}
              {description.materials && description.materials.length > 0 && (
                <ReviewField label="Getty Materials" fieldPath="describe.materials">
                  {formatControlledVocabulary(description.materials as unknown[])}
                </ReviewField>
              )}
              {description.substrate && description.substrate.length > 0 && (
                <ReviewField label="Getty Substrate" fieldPath="describe.substrate">
                  {formatControlledVocabulary(description.substrate as unknown[])}
                </ReviewField>
              )}
            </Stack>
          </ReviewSection>
        )}

      {/* 4. Provenance */}
      <ReviewSection
        title="Provenance"
        tabIndex={3}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        <Stack spacing={1}>
          {itemStatus === 'DESTROYED' && (
            <ReviewField label="Destroyed" fieldPath="provenance.destroyed">
              Yes
            </ReviewField>
          )}
          {provenance && provenance.length > 0 ? (
            provenance.map((record, i) => {
              const rowId = record.recordId ?? `idx-${i}`;
              return (
                <ReviewField
                  key={rowId}
                  label={`Provenance ${i + 1}`}
                  fieldPath={`provenance.provenanceRecords.${rowId}`}
                >
                  {truncateText(record.publicRendering || record.internalRendering || `Record ${i + 1}`)}
                </ReviewField>
              );
            })
          ) : (
            itemStatus !== 'DESTROYED' && <EmptyState />
          )}
        </Stack>
      </ReviewSection>

      {/* 5. Exhibitions — per-row visibility chip routes through
          `provenance.exhibitions.{rowId}` (existing backend per-row filter). */}
      <ReviewSection
        title="Exhibitions"
        tabIndex={4}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {exhibitionList.length > 0 ? (
          <Stack spacing={1}>
            {exhibitionList.map((ex, i) => {
              const rowId = (ex as { id?: string }).id ?? `idx-${i}`;
              return (
                <ReviewField
                  key={rowId}
                  label={`Exhibition ${i + 1}`}
                  fieldPath={`provenance.exhibitions.${rowId}`}
                >
                  {truncateText(ex.exhibitionType || 'Exhibition')}
                </ReviewField>
              );
            })}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </ReviewSection>

      {/* 6. Publications */}
      <ReviewSection
        title="Publications"
        tabIndex={5}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {publicationList.length > 0 ? (
          <Stack spacing={1}>
            {publicationList.map((pub, i) => {
              const rowId = (pub as { id?: string }).id ?? `idx-${i}`;
              return (
                <ReviewField
                  key={rowId}
                  label={`Publication ${i + 1}`}
                  fieldPath={`provenance.publications.${rowId}`}
                >
                  {truncateText(pub.publicationText || 'Publication')}
                </ReviewField>
              );
            })}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </ReviewSection>

      {/* 7. Awards & Honors */}
      <ReviewSection
        title="Awards & Honors"
        tabIndex={6}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {awardList.length > 0 ? (
          <Stack spacing={1}>
            {awardList.map((award, i) => {
              const rowId = (award as { id?: string }).id ?? `idx-${i}`;
              return (
                <ReviewField
                  key={rowId}
                  label={`Award ${i + 1}`}
                  fieldPath={`provenance.awards.${rowId}`}
                >
                  {truncateText(award.publicationText!)}
                </ReviewField>
              );
            })}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </ReviewSection>

      {/* 8. Related Works */}
      <ReviewSection
        title="Related Works"
        tabIndex={7}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {relatedWorks.length > 0 ? (
          <Stack spacing={1}>
            {relatedWorks.map((work, i) => {
              const rowId =
                (typeof work === 'object' && work !== null
                  ? (work as { id?: string }).id
                  : undefined) ?? `idx-${i}`;
              return (
                <ReviewField
                  key={rowId}
                  label={`Related Work ${i + 1}`}
                  fieldPath={`additionalInfo.relatedWorks.${rowId}`}
                >
                  {typeof work === 'string'
                    ? work
                    : (work as { title?: string }).title || 'Untitled'}
                </ReviewField>
              );
            })}
          </Stack>
        ) : (
          <EmptyState />
        )}
      </ReviewSection>

      {/* 9. Inventory & Albums (formerly "Catalog & Keywords").
          Keywords moved into the Artwork Details section above.
          Inventory & Albums is optional, so we suppress the empty-state
          CTA (text + "Go to" button) and just render the section header
          with its edit affordance when nothing is set. */}
      <ReviewSection
        title="Inventory & Albums"
        tabIndex={8}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {keywords && (
          <Stack spacing={1}>
            {keywords.customCatalogNumbers && keywords.customCatalogNumbers.length > 0 && (
              <ReviewField label="Inventory Numbers" fieldPath="catalogKeywords.customNumbers">{keywords.customCatalogNumbers.join(', ')}</ReviewField>
            )}
          </Stack>
        )}
      </ReviewSection>

      {/* 10. Always Private — condition, valuations, location, private notes, sold status */}
      <ReviewSection
        title="Always Private"
        tabIndex={9}
        onEdit={onNavigateToTab}
        isLoading={loading}
        isEmpty={false}
      >
        {(() => {
          const a = additionalInfo as (typeof additionalInfo & {
            valuations?: Array<{ value: string; currency: string; type: string }>;
            markedAsSold?: boolean;
            conditionNotes?: string;
          }) | null;
          const hasCondition = Boolean(a?.conditionNotes?.trim());
          const hasValuations = (a?.valuations?.length ?? 0) > 0;
          const hasLocation = Boolean(a?.physicalLocation?.trim());
          const hasPrivateNotes = (a?.privateNotes?.length ?? 0) > 0;
          const internalProvenanceRecords =
            provenance?.filter(
              (r) =>
                r.internalRendering?.trim() &&
                r.internalRendering.trim() !== (r.publicRendering ?? '').trim(),
            ) ?? [];
          const hasInternalProvenance = internalProvenanceRecords.length > 0;
          const nothingRecorded = !hasCondition && !hasValuations && !hasLocation && !hasPrivateNotes && !hasInternalProvenance && !a?.markedAsSold;
          if (nothingRecorded) {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Condition, valuation, storage, documents, and private notes not yet recorded.
              </Typography>
            );
          }
          // The Private Only section is structurally private end-to-end, so
          // per-field privacy chips would just repeat the same promise on
          // every row. Render plain rows here and let the section heading
          // carry the privacy context.
          return (
            <Stack spacing={1}>
              {a?.markedAsSold && (
                <ReviewField label="Status">Marked as sold</ReviewField>
              )}
              {hasLocation && (
                <ReviewField label="Current Location">{a!.physicalLocation}</ReviewField>
              )}
              {hasValuations && (
                <ReviewField label="Valuations">
                  {a!.valuations!.map((v) =>
                    `${v.value || '—'}${v.currency ? ` ${v.currency}` : ''}${v.type ? ` (${v.type})` : ''}`
                  ).join('; ')}
                </ReviewField>
              )}
              {hasPrivateNotes && (
                <ReviewField label="Private Notes">{a!.privateNotes!.join('; ')}</ReviewField>
              )}
              {hasCondition && (
                <ReviewField label="Condition / Conservation Notes">
                  {truncateText(a!.conditionNotes!.trim())}
                </ReviewField>
              )}
              {hasInternalProvenance &&
                internalProvenanceRecords.map((r, i) => (
                  <ReviewField
                    key={r.recordId ?? `internal-prov-${i}`}
                    label={`Internal Provenance ${i + 1}`}
                  >
                    {truncateText(r.internalRendering!.trim())}
                  </ReviewField>
                ))}
            </Stack>
          );
        })()}
      </ReviewSection>

      {/* View Catalog and View Object */}
      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="contained"
          onClick={() => navigate('/my-objects')}
        >
          View Catalog
        </Button>
        {catalogItemId && (
          <Button
            variant="outlined"
            onClick={() => navigate(`/object-details/${catalogItemId}`)}
          >
            View Object
          </Button>
        )}
      </Box>
    </Box>
  );
};
